// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectsLayout.tsx
// [概要] /salesprojectsmanagement 配下の親 Layout。
//        useProjectTable をここで一度だけ呼び、子ルート (一覧 / 詳細) に
//        Context 経由で共有する。子ルートが切り替わっても本 Layout は
//        マウントされたままなので、データ配列・page・検索条件・並び順・
//        スクロール位置・適用中フィルタ名が保持される。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useProjectTable, DEFAULT_FILTERS } from '../hooks/useProjectTable';
import { usePermissions } from '../firebase/PermissionProvider';
import { SalesProjectsTableContext } from '../providers/SalesProjectsTableProvider';

export default function SalesProjectsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutlet();

  const { permissionData, loading: permsLoading } = usePermissions();
  const permissions = permsLoading ? null : permissionData;

  const viewLevel = !permissions
    ? 'individual'
    : permissions.includes('readdata.sfa.all_view')
      ? 'all'
      : permissions.includes('readdata.sfa.branch_view')
        ? 'branch'
        : 'individual';

  // 初回マウント時の location.state を初期フィルター/ソートへ反映
  // (SalesProjectsFilterList から保存済み条件を適用して遷移してきた場合)
  const initialFilters = location.state?.appliedFilters || DEFAULT_FILTERS;
  const initialSort = location.state?.appliedSort || { field: 'phase', order: 'asc' };

  const tableState = useProjectTable(viewLevel, permissions, initialFilters, initialSort);

  // 適用中フィルタ名 (一覧画面の Chip 表示用)。
  // 子ルートを行き来しても保持されるよう Layout で管理する。
  const [appliedFilterName, setAppliedFilterName] = useState<string | null>(
    location.state?.appliedFilterName || null
  );

  // 一覧テーブルのスクロール位置。一覧 → 詳細 → 戻る で復元するために
  // ref として保持する (state にすると Layout が再レンダーされる)。
  const scrollPositionRef = useRef<number>(0);

  // location.state が再適用された場合 (フィルタ一覧から再度遷移してきた等) に追従。
  // 反映後は state を消して、戻るボタン等での意図しない再適用を防ぐ。
  useEffect(() => {
    if (location.state?.appliedFilterName) {
      setAppliedFilterName(location.state.appliedFilterName);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.appliedFilterName, navigate, location.pathname]);

  return (
    <SalesProjectsTableContext.Provider
      value={{
        ...tableState,
        viewLevel,
        permissions,
        appliedFilterName,
        setAppliedFilterName,
        scrollPositionRef,
      }}
    >
      {/* 一覧 ↔ 詳細 の切り替え時にフェード＆スライドのトランジションを適用。
          Layout 自体はマウントされ続けるので、Context 配下の状態
          (データ・page・検索条件・スクロール位置) は保持される。 */}
      <div style={{ overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {outlet}
          </motion.div>
        </AnimatePresence>
      </div>
    </SalesProjectsTableContext.Provider>
  );
}
